import { useCallback, useMemo } from "react";

import type { TConversation } from "@stores/chat-domain.types";
import { useChatStore } from "@stores/chat.store";
import { useChatConversations } from "@hooks/api/chat-websocket";

import {
  API_REQUEST_STATUS,
  CHAT_CONVERSATION_TYPES,
  DEFAULT_TAGS,
} from "@/utils/constants";
import { getConversationType } from "@/utils/helpers";
import { EMPTY_CONVERSATIONS } from "./utils/conversation-list.constants";

import type {
  TConversationListLogicResult,
  TConversationListViewState,
} from "./conversation-list.types";

/** Controla paginação infinita e estado de visualização (loading/data/empty) da lista de conversas. */
export const useConversationListLogic = (): TConversationListLogicResult => {
  const { fetchConversations } = useChatConversations();
  const storeSelectedTabIndex = useChatStore((s) => s.tabs.conversation);
  const conversationStatus = useChatStore((s) => s.conversation.status);
  const conversationEmpty = useChatStore((s) => s.conversation.empty);
  const conversationHasOlderChats = useChatStore((s) => s.conversation.hasOlderChats);
  const selectedTags = useChatStore((s) => s.conversation.selectedTags);

  const selectedConversation = getConversationType(storeSelectedTabIndex);
  const listKey = selectedConversation === "from" ? "conversationFrom" : "conversationTo";
  const selectedIdle = useChatStore((s) => s[listKey].idle);
  const selectedLoading = useChatStore((s) => s[listKey].loading);
  const selectedSource = useChatStore((s) => s[listKey].source);

  const loadMoreItems = useCallback(() => {
    const state = useChatStore.getState();
    const convType = getConversationType(state.tabs.conversation);

    if (state.conversation.status[convType] === API_REQUEST_STATUS.loading) return;

    let tags: string[] | null = null;
    if (convType === CHAT_CONVERSATION_TYPES.sale) {
      tags = selectedTags.length > 0 ? selectedTags : DEFAULT_TAGS;
    }

    fetchConversations(convType, state.conversation.hasOlderChats[convType], tags);
  }, [fetchConversations, selectedTags]);

  const sortedConversations = useMemo(
    () =>
      [...selectedSource].sort(
        (a: TConversation, b: TConversation) =>
          Date.parse(b.message.sentOn) - Date.parse(a.message.sentOn),
      ),
    [selectedSource],
  );

  const viewState = useMemo<TConversationListViewState>(() => {
    if ((selectedIdle || selectedLoading) && selectedSource.length === 0) {
      return { kind: "loading" };
    }

    if (selectedSource.length > 0 && sortedConversations.length > 0) {
      const hasMorePages = Boolean(conversationHasOlderChats[selectedConversation]);
      return {
        kind: "data",
        conversations: sortedConversations,
        hasMorePages,
        listItemCount: hasMorePages
          ? sortedConversations.length + 1
          : sortedConversations.length,
      };
    }

    if (
      conversationStatus[selectedConversation] === API_REQUEST_STATUS.ready &&
      conversationEmpty[selectedConversation]
    ) {
      return { kind: "empty" };
    }

    return { kind: "loading" };
  }, [
    conversationStatus,
    conversationEmpty,
    conversationHasOlderChats,
    selectedIdle,
    selectedLoading,
    selectedSource,
    selectedConversation,
    sortedConversations,
  ]);

  const conversations = viewState.kind === "data" ? viewState.conversations : EMPTY_CONVERSATIONS;
  const hasMorePages = viewState.kind === "data" && viewState.hasMorePages;

  const itemData = useMemo(
    () => ({ conversations, hasMorePages }),
    [conversations, hasMorePages],
  );

  const isItemLoaded = useCallback(
    (index: number) => !hasMorePages || index < conversations.length,
    [hasMorePages, conversations.length],
  );

  return {
    viewState,
    loadMoreItems,
    itemData,
    isItemLoaded,
    listItemCount: viewState.kind === "data" ? viewState.listItemCount : 0,
  };
};
